import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Sparkles, ArrowLeft, Building2, Loader2 } from 'lucide-react';
import { Button } from '../components/core/Button';
import { useAuth } from '../context/AuthContext';
import { initiatePayment } from '../api/subscription.api';

interface Plan {
  id: 'free_trial' | 'pro_trial' | 'pro' | 'institution';
  name: string;
  price: string;
  period: string;
  badge?: string;
  badgeColor?: string;
  pdfs: string;
  maxWritten: number;
  maxMCQ: number;
  features: string[];
  cta: string;
  recommended?: boolean;
  contactOnly?: boolean;
}

const PLANS: Plan[] = [
  {
    id: 'free_trial',
    name: 'Free Trial',
    price: '৳0',
    period: '14 days',
    badge: '14 Days Free',
    badgeColor: 'bg-emerald-500',
    pdfs: '1 PDF',
    maxWritten: 3,
    maxMCQ: 10,
    features: [
      '1 PDF per trial',
      'Max 3 written questions/PDF',
      'Max 10 MCQ questions/PDF',
      'All subjects & chapters',
      'PDF download',
    ],
    cta: 'Start Free Trial',
  },
  {
    id: 'pro_trial',
    name: 'Pro Trial',
    price: '৳499',
    period: 'per month',
    pdfs: '20 PDFs/month',
    maxWritten: 6,
    maxMCQ: 30,
    features: [
      '20 PDFs per month',
      'Max 6 written questions/PDF',
      'Max 30 MCQ questions/PDF',
      'All subjects & chapters',
      'PDF & image download',
      'Question history',
    ],
    cta: 'Get Pro Trial',
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '৳899',
    period: 'per month',
    badge: 'Best Value',
    badgeColor: 'bg-violet-600',
    pdfs: '40 PDFs/month',
    maxWritten: 6,
    maxMCQ: 30,
    features: [
      '40 PDFs per month',
      'Max 6 written questions/PDF',
      'Max 30 MCQ questions/PDF',
      'All subjects & chapters',
      'PDF & image download',
      'Question history',
      'Priority AI generation',
    ],
    cta: 'Get Pro',
    recommended: true,
  },
  {
    id: 'institution',
    name: 'Institution',
    price: 'Custom',
    period: 'contact us',
    pdfs: 'Custom volume',
    maxWritten: 0,
    maxMCQ: 0,
    features: [
      'Custom PDF volume',
      'Custom question limits',
      'Multi-user access',
      'Dedicated support',
      'Custom branding',
      'Invoice billing',
    ],
    cta: 'Contact Developer',
    contactOnly: true,
  },
];

export function PricingPage() {
  const { user, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [payLoading, setPayLoading] = useState<string | null>(null);
  const [payError, setPayError] = useState<string | null>(null);

  const handleCTA = async (plan: Plan) => {
    setPayError(null);

    if (plan.contactOnly) {
      window.location.href = 'mailto:developer@example.com?subject=Institution Plan Inquiry';
      return;
    }

    if (plan.id === 'free_trial') {
      if (!user) {
        await signInWithGoogle();
      } else {
        navigate('/');
      }
      return;
    }

    if (!user) {
      await signInWithGoogle();
      return;
    }

    setPayLoading(plan.id);
    try {
      const result = await initiatePayment(plan.id as 'pro_trial' | 'pro');
      if (result.activated) {
        navigate(`/payment/success?tran_id=${result.tranId}`);
      } else if (result.gatewayUrl) {
        window.location.href = result.gatewayUrl;
      }
    } catch (err: unknown) {
      const e = err as { message?: string };
      setPayError(e.message ?? 'Payment failed. Please try again.');
    } finally {
      setPayLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
      {/* Back */}
      <div className="max-w-6xl mx-auto mb-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-400 hover:text-white text-sm transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      </div>

      {/* Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 bg-violet-600/20 border border-violet-500/30 rounded-full px-4 py-1.5 text-violet-300 text-sm font-medium mb-4">
          <Sparkles className="w-4 h-4" /> Subscription Plans
        </div>
        <h1 className="text-4xl font-bold text-white mb-3">Choose your plan</h1>
        <p className="text-slate-400 max-w-lg mx-auto">
          Generate SSC &amp; HSC exam papers with AI. All plans include Bangla &amp; English
          support.
        </p>
      </div>

      {/* Plans grid */}
      <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {payError && (
          <div className="col-span-full mb-2 p-3 bg-red-900/30 border border-red-500/40 rounded-xl text-red-300 text-sm text-center">
            {payError}
          </div>
        )}
        {PLANS.map((plan) => (
          <div
            key={plan.id}
            className={`relative flex flex-col rounded-2xl border p-6 ${
              plan.recommended
                ? 'border-violet-500 bg-violet-600/10 shadow-violet-500/20 shadow-xl'
                : 'border-slate-700 bg-slate-800/60'
            }`}
          >
            {plan.badge && (
              <div
                className={`absolute -top-3 left-1/2 -translate-x-1/2 ${plan.badgeColor} text-white text-xs font-semibold px-3 py-1 rounded-full`}
              >
                {plan.badge}
              </div>
            )}

            {/* Plan name + price */}
            <div className="mb-5">
              <h3 className="text-lg font-semibold text-white mb-1">{plan.name}</h3>
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold text-white">{plan.price}</span>
                <span className="text-slate-400 text-sm">{plan.period}</span>
              </div>
              <div className="mt-2 text-violet-300 text-sm font-medium">{plan.pdfs}</div>
            </div>

            {/* Features */}
            <ul className="space-y-2 mb-8 flex-1">
              {plan.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-slate-300 text-sm">
                  <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            {/* CTA */}
            {plan.contactOnly ? (
              <button
                onClick={() => handleCTA(plan)}
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl border border-slate-600 text-slate-300 hover:bg-slate-700 text-sm font-medium transition-colors"
              >
                <Building2 className="w-4 h-4" />
                {plan.cta}
              </button>
            ) : (
              <Button
                onClick={() => handleCTA(plan)}
                disabled={payLoading === plan.id}
                className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                  plan.recommended
                    ? 'bg-violet-600 hover:bg-violet-500 text-white'
                    : 'bg-slate-700 hover:bg-slate-600 text-white'
                }`}
              >
                {payLoading === plan.id ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Processing…
                  </span>
                ) : (
                  plan.cta
                )}
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
